/**
 * Studio Zuiver: <tattoo-portfolio> (sections/tattoo-portfolio.liquid).
 * Wraps the shared <tattoo-gallery>: chips filter the tiles per artist, a "show more" button reveals
 * the tiles beyond the initial count. Filtering and showing more set/remove the `hidden` attribute and
 * ask the gallery to refresh (masonry layout + lightbox). The active artist is mirrored in the URL
 * hash (#portfolio-leonie) so it can be linked from an artist row. Without JS all tiles up to the
 * initial count are visible and the filter chips do nothing.
 */
class TattooPortfolio extends HTMLElement {
  #filter = '';
  #expanded = false;

  connectedCallback() {
    this.gallery = this.querySelector('tattoo-gallery');
    this.chips = Array.from(this.querySelectorAll('[data-filter]'));
    this.moreButton = this.querySelector('[data-load-more]');
    this.moreCount = this.querySelector('[data-more-count]');
    this.footer = this.moreButton?.parentElement;
    this.emptyText = this.querySelector('[data-filter-empty]');
    this.initial = parseInt(this.dataset.initial, 10) || 0;
    this.anchor = this.dataset.anchor || 'portfolio';
    if (!this.gallery) return;

    this.chips.forEach((chip) => chip.addEventListener('click', () => this.select(chip.dataset.filter, true)));
    this.moreButton?.addEventListener('click', () => {
      this.#expanded = true;
      this.#apply();
    });

    const fromHash = this.#artistFromHash();
    if (fromHash) this.select(fromHash, false);
    window.addEventListener('hashchange', this.#onHash);
  }

  disconnectedCallback() {
    window.removeEventListener('hashchange', this.#onHash);
  }

  #artistFromHash() {
    const prefix = `#${this.anchor}-`;
    if (!location.hash.startsWith(prefix)) return '';
    const handle = decodeURIComponent(location.hash.slice(prefix.length));
    return this.chips.some((chip) => chip.dataset.filter === handle) ? handle : '';
  }

  #onHash = () => {
    const handle = this.#artistFromHash();
    if (handle && handle !== this.#filter) this.select(handle, false);
  };

  /**
   * @param {string} handle - artist handle, '' for all
   * @param {boolean} pushHash - write the artist to the URL
   */
  select(handle, pushHash) {
    this.#filter = handle;
    this.#expanded = false;
    this.chips.forEach((chip) => {
      const active = chip.dataset.filter === handle;
      chip.classList.toggle('is-active', active);
      chip.setAttribute('aria-pressed', String(active));
    });
    this.#apply();
    if (pushHash) {
      const url = handle ? `#${this.anchor}-${handle}` : `#${this.anchor}`;
      history.replaceState(null, '', url);
    }
  }

  #apply() {
    const tiles = Array.from(this.gallery.querySelectorAll('a'));
    let matched = 0;
    let visible = 0;
    tiles.forEach((tile) => {
      const matches = !this.#filter || tile.dataset.artist === this.#filter;
      if (!matches) {
        tile.hidden = true;
        return;
      }
      matched += 1;
      const show = this.#expanded || !this.initial || matched <= this.initial;
      tile.hidden = !show;
      if (show) visible += 1;
    });
    const remaining = matched - visible;
    if (this.footer) this.footer.hidden = remaining <= 0;
    if (this.moreCount) this.moreCount.textContent = `(${remaining})`;
    if (this.emptyText) this.emptyText.hidden = matched > 0;
    this.gallery.refresh?.();
  }
}

if (!customElements.get('tattoo-portfolio')) {
  customElements.define('tattoo-portfolio', TattooPortfolio);
}
